import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Route, Switch, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  message,
  Popconfirm,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from "antd";
import { useTranslation } from "react-i18next";
import PageHeader from "../shared/PageHeader";
import Amount from "../shared/Amount";
import { formatCents, formatDate } from "../shared/money";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import PageToolbar from "../../../../design-system/PageToolbar";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import * as api from "./api";
import * as creditsApi from "./creditsApi";
import CustomerAccount from "../customers/CustomerAccount";
import {
  CreditSource,
  CreditStatusTag,
  OffsetModal,
  PendingRefunds,
  RefundModal,
  isUsable
} from "../customers/CreditDialogs";

const BASE = "/user/accounting/receivables";

const statusColor = {
  sent: "blue",
  partially_paid: "cyan",
  overdue: "gold",
  reminded_1: "orange",
  reminded_2: "volcano",
  reminded_3: "red",
  handed_over: "magenta"
};

const customerPath = id => `${BASE}/customers/${id}`;

const bucketColumns = t => [
  { key: "current", title: t("Not yet due") },
  { key: "d30", title: t("1–30 days") },
  { key: "d60", title: t("31–60 days") },
  { key: "d90", title: t("61–90 days") },
  { key: "d90plus", title: t("> 90 days") }
];

// ---------------------------------------------------------------------------------------------
// Offene-Posten-Liste
// ---------------------------------------------------------------------------------------------

const OpenItems = () => {
  const { t } = useTranslation("accounting");
  const history = useHistory();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Phones: a customer's invoices open in a drawer instead of an expanded table row.
  const [invoicesOf, setInvoicesOf] = useState(null);
  const [view, setView] = useState("customers");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.openItems();
      setData(res.data);
    } catch (error) {
      message.error(api.errorText(error, t("Could not load open items")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCustomerBlock = async customer => {
    try {
      await api.blockCustomerDunning(customer.customer, !customer.dunningBlocked);
      message.success(
        customer.dunningBlocked
          ? t("Dunning enabled for this customer")
          : t("Dunning blocked for this customer")
      );
      load();
    } catch (error) {
      message.error(api.errorText(error));
    }
  };

  const buckets = bucketColumns(t);
  const customerColumns = [
    {
      title: t("Customer"),
      dataIndex: "name",
      mobile: "title",
      render: (name, row) => (
        <Space direction="vertical" size={0} className="acct-wrap">
          {row.customer ? (
            <Typography.Link strong onClick={() => history.push(customerPath(row.customer))}>
              {name}
            </Typography.Link>
          ) : (
            <Typography.Text strong>{name}</Typography.Text>
          )}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.customerNumber} {row.insolvent ? <Tag color="red">{t("Insolvent")}</Tag> : null}
            {row.dunningBlocked ? <Tag color="default">{t("Dunning blocked")}</Tag> : null}
          </Typography.Text>
        </Space>
      )
    },
    ...buckets.map(b => ({
      title: b.title,
      key: b.key,
      align: "end",
      render: (_, row) => (row.buckets[b.key] ? <Amount cents={row.buckets[b.key]} /> : "–")
    })),
    {
      title: t("Dunning fees"),
      key: "fees",
      align: "end",
      render: (_, row) => (row.buckets.fees ? <Amount cents={row.buckets.fees} /> : "–")
    },
    {
      title: t("Retention"),
      key: "retention",
      align: "end",
      render: (_, row) => (row.buckets.retention ? <Amount cents={row.buckets.retention} /> : "–")
    },
    {
      title: t("Credits"),
      key: "credits",
      align: "end",
      render: (_, row) =>
        row.buckets.credits ? <Amount cents={-row.buckets.credits} signed /> : "–"
    },
    {
      title: t("Total open"),
      key: "total",
      align: "end",
      mobile: "subtitle",
      render: (_, row) => <Amount cents={row.buckets.total} strong large={isMobile} />
    },
    {
      title: "",
      key: "actions",
      render: (_, row) => (
        <Space wrap size={4}>
          {isMobile ? (
            <Button size="small" onClick={() => setInvoicesOf(row)}>
              {t("Invoices")} ({(row.invoices?.length || 0) + (row.credits?.length || 0)})
            </Button>
          ) : null}
          {row.customer ? (
            <Button size="small" onClick={() => history.push(customerPath(row.customer))}>
              {t("Account")}
            </Button>
          ) : null}
          {row.customer ? (
            <Button size="small" onClick={() => toggleCustomerBlock(row)}>
              {row.dunningBlocked ? t("Unblock dunning") : t("Block dunning")}
            </Button>
          ) : null}
        </Space>
      )
    }
  ];

  const invoiceColumns = [
    {
      title: t("Invoice number"),
      dataIndex: "invoiceNumber",
      mobile: "title",
      render: (v, row) => <a onClick={() => history.push(`/invoices/details/${row._id}`)}>{v}</a>
    },
    {
      title: t("Open"),
      dataIndex: "open",
      key: "open-mobile",
      mobile: "subtitle",
      hidden: !isMobile,
      render: v => <Amount cents={v} large />
    },
    {
      title: t("Title"),
      dataIndex: "title",
      render: v => <span className="acct-wrap">{v}</span>
    },
    {
      title: t("Branch"),
      key: "branch",
      render: (_, row) => row.branchLabel || t("Head office")
    },
    {
      title: t("Status"),
      dataIndex: "status",
      render: s => <Tag color={statusColor[s] || "default"}>{t(`status.${s}`)}</Tag>
    },
    {
      title: t("Invoice date"),
      dataIndex: "issueDate",
      render: v => <span className="acct-num">{formatDate(v)}</span>
    },
    {
      title: t("Due date"),
      dataIndex: "dueDate",
      render: v => <span className="acct-num">{formatDate(v)}</span>
    },
    {
      title: t("Days overdue"),
      dataIndex: "daysOverdue",
      align: "end",
      render: d =>
        d > 0 ? (
          <Typography.Text type={d > 30 ? "danger" : "warning"} className="acct-num">
            {d}
          </Typography.Text>
        ) : (
          "–"
        )
    },
    { title: t("Gross"), dataIndex: "gross", align: "end", render: v => <Amount cents={v} /> },
    { title: t("Paid"), dataIndex: "paid", align: "end", render: v => <Amount cents={v} /> },
    {
      title: t("Retention"),
      dataIndex: "retention",
      align: "end",
      render: (v, row) =>
        v ? (
          <Space direction="vertical" size={0} style={{ alignItems: "flex-end" }}>
            <Amount cents={v} />
            {row.retentionDueDate ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t("due {{date}}", { date: formatDate(row.retentionDueDate) })}
              </Typography.Text>
            ) : null}
          </Space>
        ) : (
          "–"
        )
    },
    {
      title: t("Open"),
      dataIndex: "open",
      align: "end",
      hidden: isMobile,
      render: v => <Amount cents={v} strong />
    },
    {
      title: t("Dunning level"),
      key: "stage",
      render: (_, row) => (row.dunning?.lastAt ? t(`stage.${row.dunning.stage}`) : "–")
    }
  ];

  // CB-1: customer credits are negative items of the customer's open items.
  const creditItemColumns = [
    {
      title: t("Credit"),
      key: "source",
      mobile: "title",
      render: (_, c) => <CreditSource credit={c} />
    },
    {
      title: t("Date"),
      dataIndex: "date",
      render: v => <span className="acct-num">{formatDate(v)}</span>
    },
    { title: t("Status"), dataIndex: "status", render: s => <CreditStatusTag status={s} /> },
    {
      title: t("Remaining"),
      dataIndex: "remaining",
      align: "end",
      mobile: "subtitle",
      render: v => <Amount cents={-v} strong signed />
    }
  ];

  // Open amount per Niederlassung of one customer ("" = head office)
  const branchTotals = invoices => {
    const map = new Map();
    for (const i of invoices || []) {
      const cur = map.get(i.branch || "") || { label: i.branchLabel || t("Head office"), open: 0 };
      cur.open += Number(i.open || 0);
      map.set(i.branch || "", cur);
    }
    return [...map.values()];
  };

  const customerItems = row => {
    const withBranches = (row.invoices || []).some(i => i.branch);
    const subtotals = withBranches ? branchTotals(row.invoices) : [];
    return (
      <>
        {subtotals.length ? (
          <Space wrap size={[8, 8]} style={{ marginBottom: 8 }}>
            {subtotals.map(b => (
              <Tag key={b.label}>
                {b.label}: <Amount cents={b.open} />
              </Tag>
            ))}
          </Space>
        ) : null}
        <ResponsiveTable
          rowKey="_id"
          size="small"
          pagination={false}
          dataSource={
            withBranches
              ? [...(row.invoices || [])].sort((a, b) =>
                  (a.branchLabel || "").localeCompare(b.branchLabel || "")
                )
              : row.invoices || []
          }
          columns={withBranches ? invoiceColumns : invoiceColumns.filter(c => c.key !== "branch")}
          locale={{ emptyText: t("No open items") }}
        />
        {row.credits?.length ? (
          <>
            <Typography.Title level={5} style={{ marginBlock: 12 }}>
              {t("Credits")}
            </Typography.Title>
            <ResponsiveTable
              rowKey="_id"
              size="small"
              pagination={false}
              dataSource={row.credits}
              columns={creditItemColumns}
            />
          </>
        ) : null}
      </>
    );
  };

  const inflowColumns = [
    {
      title: t("Week"),
      dataIndex: "week",
      render: (w, row) => `${t("Week")} ${w} (${formatDate(row.from)} – ${formatDate(row.to)})`
    },
    {
      title: t("Invoices"),
      dataIndex: "count",
      align: "end",
      render: v => <span className="acct-num">{v}</span>
    },
    {
      title: t("Expected inflow"),
      dataIndex: "amount",
      align: "end",
      mobile: "subtitle",
      render: v => <Amount cents={v} strong={isMobile} />
    }
  ];

  const totals = data?.totals || {};
  return (
    <div>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <Card size="small" className="acct-kpi">
            <Statistic title={t("Total open")} value={formatCents(totals.total || 0)} />
          </Card>
        </Col>
        {buckets.map(b => (
          <Col xs={12} sm={8} md={4} key={b.key}>
            <Card size="small" className="acct-kpi">
              <Statistic title={b.title} value={formatCents(totals[b.key] || 0)} />
            </Card>
          </Col>
        ))}
        <Col xs={12} sm={8} md={4}>
          <Card size="small" className="acct-kpi">
            <Statistic
              title={t("Retention (not due)")}
              value={formatCents(totals.retention || 0)}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small" className="acct-kpi">
            <Statistic title={t("Customer credits")} value={formatCents(-(totals.credits || 0))} />
          </Card>
        </Col>
      </Row>
      <Segmented
        block={isMobile}
        style={{ marginBottom: 16 }}
        value={view}
        onChange={setView}
        options={[
          { value: "customers", label: t("By customer") },
          {
            value: "credits",
            label: `${t("Credits (Guthaben)")}${
              data?.customers
                ? ` (${data.customers.reduce((n, c) => n + (c.credits?.length || 0), 0)})`
                : ""
            }`
          }
        ]}
      />
      {view === "credits" ? (
        <OpenCredits customers={data?.customers || []} onChanged={load} />
      ) : null}
      <div hidden={view !== "customers"}>
        <ResponsiveTable
          rowKey={row => String(row.customer || "none")}
          loading={loading}
          dataSource={data?.customers || []}
          columns={customerColumns}
          pagination={false}
          size="middle"
          expandable={
            isMobile
              ? undefined
              : {
                  expandedRowRender: customerItems
                }
          }
          summary={() =>
            data ? (
              <Table.Summary.Row>
                {/* The expand column and the customer column share the label. */}
                <Table.Summary.Cell index={0} colSpan={2}>
                  <strong>{t("Total")}</strong>
                </Table.Summary.Cell>
                {buckets.map((b, i) => (
                  <Table.Summary.Cell index={i + 2} key={b.key} align="end">
                    <Amount cents={totals[b.key] || 0} strong />
                  </Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={7} align="end">
                  <Amount cents={totals.fees || 0} strong />
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="end">
                  <Amount cents={totals.retention || 0} strong />
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} align="end">
                  <Amount cents={-(totals.credits || 0)} strong signed />
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10} align="end">
                  <Amount cents={totals.total || 0} strong />
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11} />
              </Table.Summary.Row>
            ) : null
          }
        />
        <Card size="small" title={t("Expected inflow (next 8 weeks)")} style={{ marginTop: 16 }}>
          {data?.inflow?.overdue ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              message={
                <>
                  {t("Already overdue")}: <Amount cents={data.inflow.overdue} strong />
                </>
              }
            />
          ) : null}
          <ResponsiveTable
            rowKey="week"
            size="small"
            pagination={false}
            dataSource={data?.inflow?.weeks || []}
            columns={inflowColumns}
          />
        </Card>
      </div>
      <Drawer
        open={Boolean(invoicesOf)}
        onClose={() => setInvoicesOf(null)}
        title={invoicesOf?.name || t("Invoices")}
        width={isMobile ? "100%" : 720}
        rootClassName="acct-module"
      >
        {invoicesOf ? customerItems(invoicesOf) : null}
      </Drawer>
    </div>
  );
};

// ---------------------------------------------------------------------------------------------
// Guthaben: every open customer credit, with offset and refund
// ---------------------------------------------------------------------------------------------

const OpenCredits = ({ customers, onChanged }) => {
  const { t } = useTranslation("accounting");
  const history = useHistory();
  const isMobile = useIsMobile();
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offsetOf, setOffsetOf] = useState(null);
  const [refundOf, setRefundOf] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await creditsApi.listCredits({ status: "open,refund_pending" });
      setCredits(res.data.credits || []);
    } catch (error) {
      message.error(creditsApi.errorText(error, t("Could not load the credits")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const changed = () => {
    load();
    onChanged?.();
  };

  const customerIdOf = c => String(c?.customer?._id || c?.customer || "");
  // Offset targets: the customer's open invoices from the open-items list (full open amount).
  const invoicesOf = c => {
    const row = customers.find(x => String(x.customer) === customerIdOf(c));
    return (row?.invoices || []).map(i => ({
      _id: i._id,
      invoiceNumber: i.invoiceNumber,
      open: Number(i.open || 0) + Number(i.retention || 0)
    }));
  };
  const nameOf = c =>
    c.customer?.company ||
    [c.customer?.firstName, c.customer?.lastName].filter(Boolean).join(" ") ||
    c.customer?.email ||
    "—";

  const rows = credits.filter(c => Number(c.remaining) > 0);
  const total = rows.reduce((s, c) => s + Number(c.remaining || 0), 0);
  const columns = [
    {
      title: t("Customer"),
      key: "customer",
      mobile: "title",
      render: (_, c) => (
        <Space direction="vertical" size={0} className="acct-wrap">
          <Typography.Link onClick={() => history.push(customerPath(customerIdOf(c)))}>
            {nameOf(c)}
          </Typography.Link>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {c.customer?.customerNumber}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: t("Remaining"),
      key: "remaining-mobile",
      mobile: "subtitle",
      hidden: !isMobile,
      render: (_, c) => <Amount cents={-c.remaining} large signed />
    },
    { title: t("Status"), dataIndex: "status", render: s => <CreditStatusTag status={s} /> },
    { title: t("Source"), key: "source", render: (_, c) => <CreditSource credit={c} /> },
    {
      title: t("Date"),
      dataIndex: "date",
      render: v => <span className="acct-num">{formatDate(v)}</span>
    },
    { title: t("Amount"), dataIndex: "amount", align: "end", render: v => <Amount cents={v} /> },
    {
      title: t("Remaining"),
      dataIndex: "remaining",
      align: "end",
      hidden: isMobile,
      render: v => <Amount cents={-v} strong signed />
    },
    {
      title: t("Note"),
      key: "note",
      render: (_, c) =>
        c.note || c.usages?.some(u => u.pending) ? (
          <Space direction="vertical" size={0} className="acct-wrap">
            {c.note ? <span>{c.note}</span> : null}
            <PendingRefunds credit={c} />
          </Space>
        ) : (
          "–"
        )
    },
    {
      title: "",
      key: "actions",
      render: (_, c) =>
        isUsable(c) ? (
          <div className="acct-actions">
            <Button size="small" onClick={() => setOffsetOf(c)} disabled={!invoicesOf(c).length}>
              {t("Offset")}
            </Button>
            <Button size="small" onClick={() => setRefundOf(c)}>
              {t("Refund")}
            </Button>
          </div>
        ) : null
    }
  ];

  return (
    <div>
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        size="middle"
        locale={{ emptyText: t("No open credits") }}
        summary={() =>
          rows.length ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>{t("Total")}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="end">
                <Amount cents={-total} strong signed />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} colSpan={2} />
            </Table.Summary.Row>
          ) : null
        }
      />
      <OffsetModal
        open={Boolean(offsetOf)}
        credit={offsetOf}
        invoices={offsetOf ? invoicesOf(offsetOf) : []}
        onClose={() => setOffsetOf(null)}
        onDone={changed}
      />
      <RefundModal
        open={Boolean(refundOf)}
        credit={refundOf}
        customerId={refundOf ? customerIdOf(refundOf) : null}
        onClose={() => setRefundOf(null)}
        onDone={changed}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------------------------
// Mahnläufe
// ---------------------------------------------------------------------------------------------

const runStatusColor = { proposed: "gold", confirmed: "blue", sent: "green", cancelled: "default" };

const DunningRuns = () => {
  const { t } = useTranslation("accounting");
  const history = useHistory();
  const isMobile = useIsMobile();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listRuns();
      setRuns(res.data.runs || []);
    } catch (error) {
      message.error(api.errorText(error, t("Could not load dunning runs")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const propose = async () => {
    setLoading(true);
    try {
      const res = await api.proposeRun();
      const run = res.data.run;
      if (!run.proposals.length) message.info(t("No invoices are due for a reminder today"));
      history.push(`${BASE}/dunning/${run._id}`);
    } catch (error) {
      message.error(api.errorText(error));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: t("Date"),
      dataIndex: "date",
      render: v => <span className="acct-num">{formatDate(v)}</span>
    },
    {
      title: t("Status"),
      dataIndex: "status",
      render: s => <Tag color={runStatusColor[s] || "default"}>{t(`run.${s}`)}</Tag>
    },
    {
      title: t("Proposals"),
      dataIndex: "count",
      align: "end",
      render: v => <span className="acct-num">{v}</span>
    },
    {
      title: t("Selected"),
      dataIndex: "selected",
      align: "end",
      render: v => <span className="acct-num">{v}</span>
    },
    {
      title: t("Total claim"),
      dataIndex: "total",
      align: "end",
      mobile: "subtitle",
      render: v => <Amount cents={v} strong={isMobile} large={isMobile} />
    },
    {
      title: "",
      key: "open",
      mobile: "actions",
      render: (_, row) => (
        <Button size="small" onClick={() => history.push(`${BASE}/dunning/${row._id}`)}>
          {t("Open")}
        </Button>
      )
    }
  ];

  return (
    <div>
      <PageToolbar
        actions={
          <Button type="primary" onClick={propose} loading={loading}>
            {t("Propose run")}
          </Button>
        }
      />
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        dataSource={runs}
        columns={columns}
        pagination={{ pageSize: 20 }}
        size="middle"
      />
    </div>
  );
};

const RunDetail = () => {
  const { t } = useTranslation("accounting");
  const { id } = useParams();
  const history = useHistory();
  const isMobile = useIsMobile();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getRun(id);
      setRun(res.data.run);
      setSelected(
        res.data.run.proposals.filter(p => p.selected).map(p => String(p.invoice?._id || p.invoice))
      );
    } catch (error) {
      message.error(api.errorText(error, t("Could not load the dunning run")));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const confirm = async send => {
    setLoading(true);
    try {
      const res = await api.confirmRun(id, { invoiceIds: selected, send });
      const sent = res.data.results.filter(r => r.sent).length;
      message.success(
        send ? t("Run confirmed, {{count}} reminders sent", { count: sent }) : t("Run confirmed")
      );
      setRun(res.data.run);
    } catch (error) {
      message.error(api.errorText(error));
    } finally {
      setLoading(false);
    }
  };

  const preview = proposal =>
    api
      .openBlob(api.proposalPdf(id, proposal.invoice?._id || proposal.invoice))
      .catch(error => message.error(api.errorText(error)));

  const toggleInvoiceBlock = async proposal => {
    const invoice = proposal.invoice;
    try {
      await api.blockInvoiceDunning(invoice._id, !invoice.dunning?.blocked);
      message.success(t("Saved"));
      load();
    } catch (error) {
      message.error(api.errorText(error));
    }
  };

  const editable = run?.status === "proposed";
  const rows = useMemo(
    () =>
      (run?.proposals || [])
        .map(p => ({ ...p, key: String(p.invoice?._id || p.invoice) }))
        // Grouped by customer and Niederlassung: one block per addressee
        .sort((a, b) => {
          const name = p =>
            p.customer?.company || `${p.customer?.firstName || ""} ${p.customer?.lastName || ""}`;
          return (
            name(a).localeCompare(name(b)) ||
            (a.branchLabel || "").localeCompare(b.branchLabel || "")
          );
        }),
    [run]
  );
  // Phones: selection checkboxes live on the cards (rowSelection would force a wide table).
  const mobileSelect = isMobile && editable;
  const toggleSelected = (key, on) =>
    setSelected(list => (on ? [...list.filter(k => k !== key), key] : list.filter(k => k !== key)));
  const columns = [
    {
      title: t("Customer"),
      key: "customer",
      mobile: "title",
      render: (_, p) => {
        const info = (
          <Space direction="vertical" size={0} className="acct-wrap">
            <span>
              {p.customer?.company ||
                `${p.customer?.firstName || ""} ${p.customer?.lastName || ""}`}
            </span>
            {p.branchLabel ? (
              <Typography.Text style={{ fontSize: 12 }}>
                {p.billTo === "head_office"
                  ? `${t("Head office")} · ${t("Place of service")}: ${p.branchLabel}`
                  : `${t("Branch")}: ${p.branchLabel}`}
              </Typography.Text>
            ) : null}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {p.emailTo}
            </Typography.Text>
          </Space>
        );
        return mobileSelect ? (
          <Checkbox
            checked={selected.includes(p.key)}
            onChange={e => toggleSelected(p.key, e.target.checked)}
          >
            {info}
          </Checkbox>
        ) : (
          info
        );
      }
    },
    {
      title: t("Total claim"),
      key: "total-mobile",
      mobile: "subtitle",
      hidden: !isMobile,
      render: (_, p) => <Amount cents={p.openAmount + p.fee + p.interest} large />
    },
    {
      title: t("Invoice number"),
      key: "invoice",
      render: (_, p) => (
        <a onClick={() => history.push(`/invoices/details/${p.invoice?._id}`)}>
          {p.invoice?.invoiceNumber}
        </a>
      )
    },
    {
      title: t("Due date"),
      key: "due",
      render: (_, p) => <span className="acct-num">{formatDate(p.invoice?.dueDate)}</span>
    },
    {
      title: t("Dunning level"),
      dataIndex: "stage",
      render: s => <Tag color={s >= 3 ? "red" : s >= 1 ? "orange" : "gold"}>{t(`stage.${s}`)}</Tag>
    },
    {
      title: t("Days overdue"),
      dataIndex: "daysOverdue",
      align: "end",
      render: v => <span className="acct-num">{v}</span>
    },
    { title: t("Open"), dataIndex: "openAmount", align: "end", render: v => <Amount cents={v} /> },
    { title: t("Dunning fee"), dataIndex: "fee", align: "end", render: v => <Amount cents={v} /> },
    {
      title: t("Default interest"),
      dataIndex: "interest",
      align: "end",
      render: v => <Amount cents={v} />
    },
    {
      title: t("Total claim"),
      key: "total",
      align: "end",
      hidden: isMobile,
      render: (_, p) => <Amount cents={p.openAmount + p.fee + p.interest} strong />
    },
    {
      title: t("Sent"),
      key: "sent",
      render: (_, p) => (p.sentAt ? <Tag color="green">{formatDate(p.sentAt)}</Tag> : "–")
    },
    {
      title: "",
      key: "actions",
      render: (_, p) => (
        <Space wrap size={4}>
          <Button size="small" onClick={() => preview(p)}>
            {t("PDF preview")}
          </Button>
          {editable ? (
            <Tooltip title={t("Excludes this invoice from all future dunning runs")}>
              <Button
                size="small"
                danger={!p.invoice?.dunning?.blocked}
                onClick={() => toggleInvoiceBlock(p)}
              >
                {p.invoice?.dunning?.blocked ? t("Unblock dunning") : t("Block dunning")}
              </Button>
            </Tooltip>
          ) : null}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader
        title={`${t("Dunning run")} ${run ? formatDate(run.date) : ""}`}
        subtitle={
          run ? <Tag color={runStatusColor[run.status]}>{t(`run.${run.status}`)}</Tag> : null
        }
        extra={
          <>
            <Button onClick={() => history.push(`${BASE}/dunning`)}>{t("Back")}</Button>
            {editable ? (
              <>
                <Popconfirm
                  title={t("Confirm the selected reminders without sending them?")}
                  onConfirm={() => confirm(false)}
                  disabled={!selected.length}
                >
                  <Button disabled={!selected.length} loading={loading}>
                    {t("Confirm")}
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title={t("Confirm and email the selected reminders to the customers?")}
                  onConfirm={() => confirm(true)}
                  disabled={!selected.length}
                >
                  <Button type="primary" disabled={!selected.length} loading={loading}>
                    {t("Confirm & send")}
                  </Button>
                </Popconfirm>
              </>
            ) : null}
          </>
        }
      />
      {run && !run.proposals.length ? (
        <Alert
          type="info"
          showIcon
          message={t("No invoices are due for a reminder today")}
          style={{ marginBottom: 12 }}
        />
      ) : null}
      {mobileSelect && rows.length ? (
        <Checkbox
          style={{ marginBottom: 12 }}
          checked={selected.length === rows.length}
          indeterminate={selected.length > 0 && selected.length < rows.length}
          onChange={e => setSelected(e.target.checked ? rows.map(r => r.key) : [])}
        >
          {t("All")} ({selected.length}/{rows.length})
        </Checkbox>
      ) : null}
      <ResponsiveTable
        rowKey="key"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={false}
        size="middle"
        rowSelection={
          editable && !isMobile
            ? { selectedRowKeys: selected, onChange: keys => setSelected(keys.map(String)) }
            : undefined
        }
      />
    </div>
  );
};

// ---------------------------------------------------------------------------------------------

const Receivables = () => {
  const { t } = useTranslation("accounting");
  const history = useHistory();
  const tab = window.location.pathname.startsWith(`${BASE}/dunning`) ? "dunning" : "open";
  return (
    <Switch>
      <Route path={`${BASE}/dunning/:id`} component={RunDetail} />
      <Route path={`${BASE}/customers/:id`}>
        <CustomerAccount backTo={BASE} />
      </Route>
      <Route path={BASE}>
        <PageHeader
          title={t("Open items & dunning")}
          subtitle={t("Outstanding customer invoices, aging and reminder runs")}
        />
        <Tabs
          activeKey={tab}
          onChange={key => history.push(key === "dunning" ? `${BASE}/dunning` : BASE)}
          items={[
            { key: "open", label: t("Open items"), children: <OpenItems /> },
            { key: "dunning", label: t("Dunning runs"), children: <DunningRuns /> }
          ]}
        />
      </Route>
    </Switch>
  );
};

export default Receivables;
